import { z } from 'zod';
import { PlatformError } from './errors.ts';

/**
 * Decimal strings for money and prices (added by 23). Never use floats for money: every helper here works on exact
 * base-10 strings through `bigint`. Accepted input: an optional `-`, digits, an optional fraction and an optional
 * exponent (`3e-6`, as some providers report). Output is always canonical: plain notation, no exponent, no trailing
 * fraction zeros, no leading zeros, `0` for zero (never `-0`).
 */
const DECIMAL_INPUT = /^(-)?(\d+)(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/;

/** A non-negative decimal string in plain notation, e.g. `0.000003`, `12`, `1.5`. Prices and costs. */
export const DecimalString = z
  .string()
  .max(64)
  .regex(/^\d+(?:\.\d+)?$/, 'must be a non-negative decimal string, e.g. 0.000003')
  .meta({ id: 'DecimalString' });
export type DecimalString = z.infer<typeof DecimalString>;

interface Scaled {
  /** Unscaled integer value. */
  units: bigint;
  /** Number of fraction digits. */
  scale: number;
}

/** Longest exponent accepted; protects against `1e999999999` building huge strings. */
const MAX_EXPONENT = 64;

function parse(value: string): Scaled {
  const match = DECIMAL_INPUT.exec(value.trim());
  if (!match) throw new PlatformError('validation_failed', `Not a decimal number: ${JSON.stringify(value)}`);
  const [, sign, int = '0', frac = '', exp] = match;
  const exponent = exp === undefined ? 0 : Number.parseInt(exp, 10);
  if (Math.abs(exponent) > MAX_EXPONENT)
    throw new PlatformError('validation_failed', `Decimal exponent out of range: ${JSON.stringify(value)}`);
  let units = BigInt(`${int}${frac}`);
  let scale = frac.length - exponent;
  if (scale < 0) {
    units *= 10n ** BigInt(-scale);
    scale = 0;
  }
  if (sign === '-') units = -units;
  return { units, scale };
}

function format({ units, scale }: Scaled): string {
  const negative = units < 0n;
  let digits = (negative ? -units : units).toString();
  if (scale > 0) {
    digits = digits.padStart(scale + 1, '0');
    let int = digits.slice(0, digits.length - scale);
    let frac = digits.slice(digits.length - scale);
    frac = frac.replace(/0+$/, '');
    int = int.replace(/^0+(?=\d)/, '');
    digits = frac.length > 0 ? `${int}.${frac}` : int;
  }
  if (/^0(?:\.0*)?$/.test(digits)) return '0';
  return negative ? `-${digits}` : digits;
}

function align(a: Scaled, b: Scaled): [bigint, bigint, number] {
  const scale = Math.max(a.scale, b.scale);
  return [a.units * 10n ** BigInt(scale - a.scale), b.units * 10n ** BigInt(scale - b.scale), scale];
}

/** True when `value` parses as a decimal number (plain or exponent notation). */
export function isDecimal(value: string): boolean {
  return DECIMAL_INPUT.test(value.trim());
}

/** Canonical plain form: `'3e-6'` → `'0.000003'`, `'1.50'` → `'1.5'`, `'-0'` → `'0'`. */
export function normalizeDecimal(value: string): string {
  return format(parse(value));
}

/** Exact sum of decimal strings (`decimalAdd('0.1', '0.2') === '0.3'`). No argument → `'0'`. */
export function decimalAdd(...values: string[]): string {
  let acc: Scaled = { units: 0n, scale: 0 };
  for (const value of values) {
    const [x, y, scale] = align(acc, parse(value));
    acc = { units: x + y, scale };
  }
  return format(acc);
}

/** Exact product of two decimal strings. */
export function decimalMultiply(a: string, b: string): string {
  const x = parse(a);
  const y = parse(b);
  return format({ units: x.units * y.units, scale: x.scale + y.scale });
}

/**
 * Cost of `count` tokens at a per-token price: `decimalTimesCount('0.000003', 1200) === '0.0036'`. `count` must be a
 * non-negative safe integer (a token count), never a fractional number.
 */
export function decimalTimesCount(perUnitPrice: string, count: number): string {
  if (!Number.isSafeInteger(count) || count < 0)
    throw new PlatformError('validation_failed', `Token count must be a non-negative integer, got ${count}`);
  const x = parse(perUnitPrice);
  return format({ units: x.units * BigInt(count), scale: x.scale });
}

/** Shifts the decimal point: `decimalShift('3', -6) === '0.000003'`, `decimalShift('0.000003', 6) === '3'`. */
export function decimalShift(value: string, places: number): string {
  if (!Number.isInteger(places)) throw new PlatformError('validation_failed', 'places must be an integer');
  const x = parse(value);
  const scale = x.scale - places;
  return scale >= 0
    ? format({ units: x.units, scale })
    : format({ units: x.units * 10n ** BigInt(-scale), scale: 0 });
}

/** Per-token price → per-million-tokens price (OpenRouter reports per token): `'0.000003'` → `'3'`. */
export function perTokenToPerMillion(perToken: string): string {
  return decimalShift(perToken, 6);
}

/** Per-million-tokens price → per-token price: `'3'` → `'0.000003'`. */
export function perMillionToPerToken(perMillion: string): string {
  return decimalShift(perMillion, -6);
}

/** -1, 0 or 1 as `a` is below, equal to or above `b`. */
export function decimalCompare(a: string, b: string): -1 | 0 | 1 {
  const [x, y] = align(parse(a), parse(b));
  return x < y ? -1 : x > y ? 1 : 0;
}

/**
 * Rounds half away from zero to at most `places` fraction digits, for display only (`decimalRound('0.00365', 4)` →
 * `'0.0037'`). Stored values keep full precision.
 */
export function decimalRound(value: string, places: number): string {
  if (!Number.isInteger(places) || places < 0)
    throw new PlatformError('validation_failed', 'places must be a non-negative integer');
  const x = parse(value);
  if (x.scale <= places) return format(x);
  const divisor = 10n ** BigInt(x.scale - places);
  const negative = x.units < 0n;
  const abs = negative ? -x.units : x.units;
  let quotient = abs / divisor;
  if ((abs % divisor) * 2n >= divisor) quotient += 1n;
  return format({ units: negative ? -quotient : quotient, scale: places });
}
